import { useState } from 'react';
import { Sparkles, ArrowRight, Check } from 'lucide-react';

/**
 * Design System Preview — 5 specimens that maximize style differentiation:
 * 1. Button Group (3 buttons: primary, secondary, ghost) — with hover states
 * 2. Card Stack (2 overlapping cards showing depth/shadows/blur) — with hover lift
 * 3. Form Input (input + label + helper showing borders/radius/focus) — with focus ring
 * 4. Toggle Row (pill tabs showing state treatment/pressed effects)
 * 5. Hover & Motion (interactive card + animation personality indicator)
 */

function getStyleCSS(styleName, { primary, bg, text, accent, secondary }) {
  const s = (styleName || '').toLowerCase();

  if (s.includes('neumorphism') || s.includes('soft ui'))
    return {
      r: '14px', shadow: '-5px -5px 15px rgba(255,255,255,0.07), 5px 5px 15px rgba(0,0,0,0.25)',
      border: 'none', btnR: '12px', btnShadow: '-3px -3px 8px rgba(255,255,255,0.05), 3px 3px 8px rgba(0,0,0,0.2)',
      ghostBorder: 'none', ghostShadow: 'inset -2px -2px 5px rgba(255,255,255,0.05), inset 2px 2px 5px rgba(0,0,0,0.15)',
      inputShadow: 'inset -2px -2px 5px rgba(255,255,255,0.05), inset 2px 2px 5px rgba(0,0,0,0.15)',
      inputBorder: 'none', pillR: '100px', backdrop: '',
      // Hover & motion
      hoverLift: '-2px', hoverShadow: '-5px -5px 18px rgba(255,255,255,0.09), 5px 5px 18px rgba(0,0,0,0.3)',
      hoverTransition: '200ms', btnHoverBrightness: 1.05, personality: 'soft, cushioned, tactile',
    };
  if (s.includes('glassmorphism') || s.includes('glass'))
    return {
      r: '16px', shadow: '0 8px 32px rgba(0,0,0,0.12)',
      border: '1px solid rgba(255,255,255,0.18)', btnR: '12px', btnShadow: 'none',
      ghostBorder: '1px solid rgba(255,255,255,0.2)', ghostShadow: 'none',
      inputShadow: 'none', inputBorder: '1px solid rgba(255,255,255,0.15)',
      pillR: '100px', backdrop: 'blur(16px)', bgAlpha: 'bb',
      // Hover & motion
      hoverLift: '-2px', hoverShadow: '0 12px 40px rgba(0,0,0,0.18)',
      hoverTransition: '200ms', btnHoverBrightness: 1.08, personality: 'smooth, flowing, layered',
      hoverBackdrop: 'blur(20px)',
    };
  if (s.includes('neubrutalism') || s.includes('neu brutalism'))
    return {
      r: '2px', shadow: `5px 5px 0px ${accent}`,
      border: `2.5px solid ${text}`, btnR: '2px', btnShadow: `3px 3px 0px ${accent}`,
      ghostBorder: `2.5px solid ${text}`, ghostShadow: `3px 3px 0px ${text}40`,
      inputShadow: `3px 3px 0px ${text}20`, inputBorder: `2.5px solid ${text}`,
      pillR: '2px', backdrop: '',
      // Hover & motion
      hoverLift: '0', hoverShadow: `7px 7px 0px ${accent}`,
      hoverTransition: '100ms', btnHoverBrightness: 1, personality: 'snappy, instant, aggressive',
      shadowShift: true,
    };
  if (s.includes('brutalism'))
    return {
      r: '0px', shadow: '5px 5px 0px #000',
      border: '2.5px solid #000', btnR: '0px', btnShadow: '3px 3px 0px #000',
      ghostBorder: '2.5px solid #000', ghostShadow: 'none',
      inputShadow: 'none', inputBorder: '2.5px solid #000',
      pillR: '0px', backdrop: '',
      // Hover & motion
      hoverLift: '0', hoverShadow: '7px 7px 0px #000',
      hoverTransition: '100ms', btnHoverBrightness: 1, personality: 'snappy, instant, decisive',
      shadowShift: true,
    };
  if (s.includes('editorial'))
    return {
      r: '0px', shadow: 'none',
      border: 'none', borderBottom: `1px solid ${text}25`, btnR: '0px', btnShadow: 'none',
      ghostBorder: 'none', ghostShadow: 'none', ghostUnderline: true,
      inputShadow: 'none', inputBorder: 'none', inputBorderBottom: `1px solid ${text}30`,
      pillR: '0px', backdrop: '', letterSpacing: '0.1em',
      // Hover & motion
      hoverLift: '0', hoverShadow: 'none',
      hoverTransition: '250ms', btnHoverBrightness: 1, personality: 'measured, editorial, refined',
      hoverBorderBottom: `1px solid ${text}50`,
    };
  if (s.includes('luxury'))
    return {
      r: '2px', shadow: '0 4px 30px rgba(0,0,0,0.06)',
      border: `0.5px solid ${text}12`, btnR: '2px', btnShadow: 'none',
      ghostBorder: `0.5px solid ${text}30`, ghostShadow: 'none',
      inputShadow: 'none', inputBorder: `0.5px solid ${text}20`,
      pillR: '2px', backdrop: '', letterSpacing: '0.12em',
      // Hover & motion
      hoverLift: '-1px', hoverShadow: '0 6px 40px rgba(0,0,0,0.08)',
      hoverTransition: '300ms', btnHoverBrightness: 1.02, personality: 'slow, graceful, unhurried',
    };
  if (s.includes('dark mode') || s.includes('dark'))
    return {
      r: '10px', shadow: `0 0 24px ${primary}20`,
      border: '1px solid rgba(255,255,255,0.1)', btnR: '8px', btnShadow: `0 0 14px ${primary}35`,
      ghostBorder: '1px solid rgba(255,255,255,0.15)', ghostShadow: 'none',
      inputShadow: 'none', inputBorder: '1px solid rgba(255,255,255,0.1)',
      pillR: '100px', backdrop: '',
      // Hover & motion
      hoverLift: '-2px', hoverShadow: `0 0 32px ${primary}35`,
      hoverTransition: '200ms', btnHoverBrightness: 1.1, personality: 'precise, technical, crisp',
      glowOnHover: true,
    };
  if (s.includes('bento'))
    return {
      r: '14px', shadow: '0 1px 4px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)', btnR: '10px', btnShadow: 'none',
      ghostBorder: '1px solid rgba(0,0,0,0.1)', ghostShadow: 'none',
      inputShadow: 'none', inputBorder: '1px solid rgba(0,0,0,0.08)',
      pillR: '10px', backdrop: '',
      // Hover & motion
      hoverLift: '-2px', hoverShadow: '0 4px 12px rgba(0,0,0,0.12)',
      hoverTransition: '200ms', btnHoverBrightness: 1.05, personality: 'balanced, modular, clean',
    };
  if (s.includes('vibrant') || s.includes('block'))
    return {
      r: '10px', shadow: 'none',
      border: 'none', btnR: '10px', btnShadow: 'none',
      ghostBorder: `2px solid ${primary}`, ghostShadow: 'none',
      inputShadow: 'none', inputBorder: `2px solid ${primary}40`,
      pillR: '10px', backdrop: '',
      // Hover & motion
      hoverLift: '-3px', hoverShadow: `0 8px 20px ${primary}25`,
      hoverTransition: '150ms', btnHoverBrightness: 1.08, personality: 'energetic, bold, vivid',
    };
  if (s.includes('motion') || s.includes('kinetic'))
    return {
      r: '16px', shadow: '0 6px 20px rgba(0,0,0,0.1)',
      border: 'none', btnR: '100px', btnShadow: 'none',
      ghostBorder: `1.5px solid ${text}30`, ghostShadow: 'none',
      inputShadow: 'none', inputBorder: `1.5px solid ${text}20`,
      pillR: '100px', backdrop: '',
      // Hover & motion
      hoverLift: '-4px', hoverShadow: '0 12px 30px rgba(0,0,0,0.15)',
      hoverTransition: '200ms', btnHoverBrightness: 1.06, personality: 'kinetic, bouncy, expressive',
      hoverScale: 1.02,
    };
  // Default: minimalism
  return {
    r: '0px', shadow: 'none',
    border: `1px solid ${text}10`, btnR: '4px', btnShadow: 'none',
    ghostBorder: `1px solid ${text}25`, ghostShadow: 'none',
    inputShadow: 'none', inputBorder: `1px solid ${text}20`,
    pillR: '2px', backdrop: '',
    // Hover & motion
    hoverLift: '-1px', hoverShadow: '0 2px 8px rgba(0,0,0,0.06)',
    hoverTransition: '200ms', btnHoverBrightness: 1, personality: 'subtle, minimal, intentional',
  };
}

export default function DesignSystemPreview({ colors, headingFont, bodyFont, companyName, borderRadius, className, styleName }) {
  const primary = colors?.primary || '#2563EB';
  const bg = colors?.background || '#FFFFFF';
  const text = colors?.text || '#1E293B';
  const accent = colors?.accent || '#F59E0B';
  const secondary = colors?.secondary || '#64748B';

  const hFam = headingFont ? `"${headingFont}", sans-serif` : 'system-ui, sans-serif';
  const bFam = bodyFont ? `"${bodyFont}", sans-serif` : 'system-ui, sans-serif';

  const ss = getStyleCSS(styleName, { primary, bg, text, accent, secondary });
  const r = borderRadius || ss.r;

  // Hover state tracking
  const [hovered, setHovered] = useState(null);

  const isGlass = ss.backdrop;
  const panelClass = "rounded-xl border border-[rgba(255,255,255,0.09)] bg-[var(--surface-1)] p-4 transition-all duration-200";
  const labelClass = "text-[11px] text-[rgba(240,239,232,0.5)] mb-3 font-mono uppercase tracking-[0.1em] block";

  // Glass styles need a vibrant background behind cards for blur to be visible
  const glassContainerStyle = isGlass ? {
    background: `linear-gradient(135deg, ${primary}30, ${accent || secondary}25, ${primary}15)`,
    borderRadius: r, padding: 12, position: 'relative',
  } : {};

  const cardBg = ss.bgAlpha ? `${bg}${ss.bgAlpha}` : bg;
  const cardBase = {
    backgroundColor: cardBg, borderRadius: r,
    boxShadow: ss.shadow, border: ss.border || 'none',
    ...(ss.backdrop ? { backdropFilter: ss.backdrop, WebkitBackdropFilter: ss.backdrop } : {}),
    ...(ss.borderBottom ? { borderBottom: ss.borderBottom } : {}),
  };

  // Transition style applied to all interactive specimens
  const tx = { transition: `all ${ss.hoverTransition || '200ms'} ease` };

  // Compute hover styles for front card
  const cardHoverStyle = hovered === 'card' ? {
    transform: `translateY(${ss.hoverLift || '-2px'})${ss.hoverScale ? ` scale(${ss.hoverScale})` : ''}`,
    boxShadow: ss.hoverShadow || ss.shadow,
    ...(ss.hoverBackdrop ? { backdropFilter: ss.hoverBackdrop, WebkitBackdropFilter: ss.hoverBackdrop } : {}),
    ...(ss.hoverBorderBottom ? { borderBottom: ss.hoverBorderBottom } : {}),
  } : {};

  // Button hover
  const btnHover = (id) => hovered === id ? {
    filter: `brightness(${ss.btnHoverBrightness || 1.05})`,
    boxShadow: ss.hoverShadow ? ss.btnShadow : (ss.btnShadow || 'none'),
    transform: 'scale(1.03)',
  } : {};

  // Ghost button hover
  const ghostHover = hovered === 'ghost' ? {
    backgroundColor: `${primary}08`,
    ...(ss.ghostUnderline ? { textDecorationColor: primary } : {}),
  } : {};

  return (
    <div className={className || "grid grid-cols-2 gap-4"}>

      {/* ── 1. Button Group ── */}
      <div className={panelClass}>
        <p className={labelClass}>Buttons</p>
        <div style={glassContainerStyle}>
        <div style={{ ...cardBase, padding: 20 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Primary */}
            <button
              onMouseEnter={() => setHovered('btn-primary')}
              onMouseLeave={() => setHovered(null)}
              style={{
                ...tx,
                backgroundColor: primary, color: '#fff', fontSize: 12, fontWeight: 500,
                padding: '8px 18px', borderRadius: ss.btnR, fontFamily: bFam,
                boxShadow: ss.btnShadow || 'none', border: 'none', cursor: 'pointer',
                ...(ss.letterSpacing ? { letterSpacing: ss.letterSpacing, textTransform: 'uppercase', fontSize: 10 } : {}),
                ...btnHover('btn-primary'),
              }}>Primary</button>
            {/* Secondary */}
            <button
              onMouseEnter={() => setHovered('btn-secondary')}
              onMouseLeave={() => setHovered(null)}
              style={{
                ...tx,
                backgroundColor: `${primary}15`, color: primary, fontSize: 12, fontWeight: 500,
                padding: '8px 18px', borderRadius: ss.btnR, fontFamily: bFam,
                border: ss.ghostBorder || `1px solid ${primary}30`,
                boxShadow: ss.ghostShadow || 'none', cursor: 'pointer',
                ...(ss.letterSpacing ? { letterSpacing: ss.letterSpacing, textTransform: 'uppercase', fontSize: 10 } : {}),
                ...btnHover('btn-secondary'),
              }}>Secondary</button>
            {/* Ghost */}
            <button
              onMouseEnter={() => setHovered('ghost')}
              onMouseLeave={() => setHovered(null)}
              style={{
                ...tx,
                backgroundColor: 'transparent', color: text, fontSize: 12, fontWeight: 400,
                padding: '8px 18px', borderRadius: ss.btnR, fontFamily: bFam,
                border: ss.ghostBorder || `1px solid ${text}25`,
                boxShadow: 'none', cursor: 'pointer',
                ...(ss.ghostUnderline ? { border: 'none', textDecoration: 'underline', textUnderlineOffset: '4px', padding: '8px 4px' } : {}),
                ...(ss.letterSpacing ? { letterSpacing: ss.letterSpacing, textTransform: 'uppercase', fontSize: 10 } : {}),
                ...ghostHover,
              }}>
              Ghost <ArrowRight style={{ width: 12, height: 12, display: 'inline', verticalAlign: 'middle', ...tx, ...(hovered === 'ghost' ? { transform: 'translateX(3px)' } : {}) }} />
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* ── 2. Cards & Images — Relume-style 3 variants ── */}
      <div className={panelClass}>
        <p className={labelClass}>Cards & Images</p>
        <div style={glassContainerStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>

          {/* Card 1: Image only — fills entire card */}
          <div
            onMouseEnter={() => setHovered('card-img')}
            onMouseLeave={() => setHovered(null)}
            style={{
              borderRadius: r, overflow: 'hidden', cursor: 'pointer',
              border: ss.border || 'none', boxShadow: ss.shadow,
              ...tx,
              ...(hovered === 'card-img' ? { transform: `translateY(${ss.hoverLift || '-2px'})`, boxShadow: ss.hoverShadow || ss.shadow } : {}),
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&q=80"
              alt="Sample"
              style={{ width: '100%', height: '100%', minHeight: 140, objectFit: 'cover', display: 'block' }}
            />
          </div>

          {/* Card 2: Image + title + description (Default Card) */}
          <div
            onMouseEnter={() => setHovered('card-default')}
            onMouseLeave={() => setHovered(null)}
            style={{
              ...cardBase, ...tx, overflow: 'hidden', cursor: 'pointer',
              ...(hovered === 'card-default' ? { transform: `translateY(${ss.hoverLift || '-2px'})`, boxShadow: ss.hoverShadow || ss.shadow } : {}),
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=200&fit=crop&q=80"
              alt="Card image"
              style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }}
            />
            <div style={{ padding: 12 }}>
              <span style={{ fontFamily: hFam, color: text, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 3 }}>
                {companyName || 'Default Card'}
              </span>
              <p style={{ fontFamily: bFam, color: secondary, fontSize: 10, lineHeight: 1.5 }}>
                Pick a card style that matches your overall aesthetic
              </p>
            </div>
          </div>

          {/* Card 3: Text only with heading + description + button */}
          <div
            onMouseEnter={() => setHovered('card-text')}
            onMouseLeave={() => setHovered(null)}
            style={{
              ...cardBase, ...tx, overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              ...(hovered === 'card-text' ? { transform: `translateY(${ss.hoverLift || '-2px'})`, boxShadow: ss.hoverShadow || ss.shadow } : {}),
            }}
          >
            <div style={{ padding: 12 }}>
              <span style={{ fontFamily: hFam, color: text, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4, lineHeight: 1.3 }}>
                Medium length section heading
              </span>
              <p style={{ fontFamily: bFam, color: secondary, fontSize: 10, lineHeight: 1.5 }}>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </p>
            </div>
            <div style={{ padding: '0 12px 12px' }}>
              <div style={{
                backgroundColor: primary, color: bg === '#FFFFFF' || bg === '#fff' ? '#FFFFFF' : '#FFFFFF',
                fontSize: 10, fontWeight: 500, fontFamily: bFam,
                padding: '6px 14px', borderRadius: ss.btnR, display: 'inline-block',
                ...(ss.letterSpacing ? { letterSpacing: ss.letterSpacing, textTransform: 'uppercase', fontSize: 9 } : {}),
              }}>
                Button
              </div>
            </div>
          </div>

        </div>
        </div>
      </div>

      {/* ── 3. Form Input ── */}
      <div className={panelClass}>
        <p className={labelClass}>Form Input</p>
        <div style={glassContainerStyle}>
        <div style={{ ...cardBase, padding: 20 }}>
          <label style={{ fontFamily: bFam, color: text, fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 6 }}>
            Email address
          </label>
          <div
            onMouseEnter={() => setHovered('input')}
            onMouseLeave={() => setHovered(null)}
            style={{
              ...tx,
              backgroundColor: ss.inputBorder === 'none' ? `${text}06` : cardBg,
              border: hovered === 'input' ? `1px solid ${primary}` : (ss.inputBorder || `1px solid ${text}20`),
              borderRadius: r, padding: '8px 12px',
              boxShadow: hovered === 'input' ? `0 0 0 3px ${primary}15` : (ss.inputShadow || 'none'),
              cursor: 'text',
              ...(ss.inputBorderBottom && hovered !== 'input' ? { border: 'none', borderBottom: ss.inputBorderBottom, borderRadius: 0 } : {}),
              ...(ss.inputBorderBottom && hovered === 'input' ? { border: 'none', borderBottom: `1px solid ${primary}`, borderRadius: 0, boxShadow: `0 2px 0 0 ${primary}30` } : {}),
            }}>
            <span style={{ fontFamily: bFam, color: `${text}40`, fontSize: 12 }}>name@company.com</span>
          </div>
          <p style={{ fontFamily: bFam, color: secondary, fontSize: 10, marginTop: 6 }}>
            We'll never share your email.
          </p>
        </div>
        </div>
      </div>

      {/* ── 4. Toggle / Pill Tabs ── */}
      <div className={panelClass}>
        <p className={labelClass}>Tabs & State</p>
        <div style={glassContainerStyle}>
        <div style={{ ...cardBase, padding: 20 }}>
          {/* Pill tab group — clickable */}
          <div style={{
            display: 'inline-flex', gap: 2, padding: 3,
            backgroundColor: `${text}08`, borderRadius: ss.pillR,
          }}>
            {['Monthly', 'Yearly'].map((tab) => {
              const isActive = (hovered === `tab-${tab}`) !== (tab === 'Monthly'); // toggle: Monthly default active, click swaps
              return (
                <div
                  key={tab}
                  onClick={() => setHovered(hovered === `tab-${tab}` ? null : `tab-${tab}`)}
                  style={{
                    ...tx,
                    padding: '6px 16px', borderRadius: ss.pillR,
                    fontSize: 11, fontFamily: bFam, fontWeight: isActive ? 500 : 400,
                    backgroundColor: isActive ? primary : 'transparent',
                    color: isActive ? '#fff' : secondary,
                    boxShadow: isActive ? (ss.btnShadow || 'none') : 'none',
                    cursor: 'pointer',
                    ...(ss.letterSpacing ? { letterSpacing: ss.letterSpacing, textTransform: 'uppercase', fontSize: 9 } : {}),
                  }}
                >{tab}</div>
              );
            })}
          </div>
          {/* Checkbox — clickable */}
          <div
            onClick={() => setHovered(hovered === 'checkbox' ? null : 'checkbox')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, cursor: 'pointer' }}
          >
            <div style={{
              ...tx,
              width: 16, height: 16, borderRadius: Math.min(parseInt(r) || 0, 4) + 'px',
              backgroundColor: hovered === 'checkbox' ? `${text}15` : primary,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: ss.btnShadow || 'none',
              border: hovered === 'checkbox' ? `1.5px solid ${text}30` : 'none',
            }}>
              {hovered !== 'checkbox' && <Check style={{ width: 10, height: 10, color: '#fff' }} />}
            </div>
            <span style={{ fontFamily: bFam, color: text, fontSize: 12 }}>Annual discount applied</span>
          </div>
          {/* Color dots */}
          <div style={{ display: 'flex', gap: 10, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${text}10` }}>
            {[primary, accent, secondary, text].map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{
                  width: 10, height: 10,
                  borderRadius: ss.pillR === '0px' ? '0px' : '50%',
                  backgroundColor: c, border: `1px solid ${text}15`,
                }} />
                <span style={{ fontFamily: bFam, color: secondary, fontSize: 9 }}>{c}</span>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>

      {/* ── 5. Hover & Animation ── */}
      <div className={`${panelClass} col-span-2`}>
        <p className={labelClass}>Animation — <span style={{ color: 'rgba(240,239,232,0.35)', fontStyle: 'italic', textTransform: 'none', letterSpacing: 0 }}>hover to preview</span></p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
          {/* Four demo elements — each shows a different animation behavior */}

          {/* Card lift + shadow */}
          <div
            onMouseEnter={() => setHovered('anim-lift')}
            onMouseLeave={() => setHovered(null)}
            style={{
              flex: 1,
              transition: `all ${ss.hoverTransition || '200ms'} cubic-bezier(0.22, 1, 0.36, 1)`,
              backgroundColor: cardBg, borderRadius: r, padding: 14,
              border: ss.border || `1px solid ${text}12`,
              boxShadow: hovered === 'anim-lift'
                ? (ss.glowOnHover ? `0 0 32px ${primary}35` : (ss.hoverShadow || `0 8px 25px ${text}15`))
                : (ss.shadow || `0 1px 3px ${text}08`),
              transform: hovered === 'anim-lift'
                ? `translateY(${ss.hoverLift === '0' ? '0' : (ss.hoverLift || '-4px')})${ss.hoverScale ? ` scale(${ss.hoverScale})` : ''}`
                : 'translateY(0) scale(1)',
              cursor: 'pointer',
              ...(ss.backdrop ? { backdropFilter: hovered === 'anim-lift' ? (ss.hoverBackdrop || ss.backdrop) : ss.backdrop, WebkitBackdropFilter: hovered === 'anim-lift' ? (ss.hoverBackdrop || ss.backdrop) : ss.backdrop } : {}),
              ...(ss.shadowShift && hovered === 'anim-lift' ? { boxShadow: ss.hoverShadow } : {}),
            }}
          >
            <div style={{
              width: 24, height: 24, borderRadius: ss.btnR,
              backgroundColor: hovered === 'anim-lift' ? primary : `${primary}15`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 8, transition: `all ${ss.hoverTransition || '200ms'} ease`,
            }}>
              <Sparkles style={{ width: 12, height: 12, color: hovered === 'anim-lift' ? '#fff' : primary, transition: 'color 200ms ease' }} />
            </div>
            <span style={{ fontFamily: hFam, color: text, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>
              {companyName || 'Card'}
            </span>
            <span style={{ fontFamily: 'var(--orbis-mono)', fontSize: 8, color: secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {ss.hoverLift === '0' ? 'Shadow shift' : `Lift ${ss.hoverLift || '-4px'}`}
            </span>
          </div>

          {/* Button press */}
          <div
            onMouseEnter={() => setHovered('anim-press')}
            onMouseLeave={() => setHovered(null)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
              backgroundColor: cardBg, borderRadius: r, padding: 14,
              border: ss.border || `1px solid ${text}12`,
              cursor: 'pointer', minHeight: 100,
            }}
          >
            <div style={{
              transition: `all ${ss.hoverTransition || '200ms'} cubic-bezier(0.22, 1, 0.36, 1)`,
              backgroundColor: primary, color: '#fff', fontSize: 11, fontWeight: 500,
              padding: '8px 20px', borderRadius: ss.btnR, fontFamily: bFam,
              boxShadow: hovered === 'anim-press' ? 'none' : (ss.btnShadow || 'none'),
              border: 'none',
              transform: hovered === 'anim-press' ? 'scale(0.95)' : 'scale(1)',
              ...(ss.letterSpacing ? { letterSpacing: ss.letterSpacing, textTransform: 'uppercase', fontSize: 9 } : {}),
            }}>
              Press me
            </div>
            <span style={{ fontFamily: 'var(--orbis-mono)', fontSize: 8, color: secondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 8 }}>
              Active: scale(0.95)
            </span>
          </div>

          {/* Scroll entrance simulation */}
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6,
            backgroundColor: cardBg, borderRadius: r, padding: 14,
            border: ss.border || `1px solid ${text}12`,
            overflow: 'hidden',
          }}>
            {[0, 1, 2].map(i => (
              <div
                key={i}
                style={{
                  height: 8, borderRadius: r,
                  backgroundColor: i === 0 ? primary : `${text}${i === 1 ? '15' : '08'}`,
                  width: i === 0 ? '70%' : i === 1 ? '100%' : '45%',
                  animation: `slideInOut ${ss.hoverTransition === '100ms' ? '1.5s' : ss.hoverTransition === '300ms' ? '3s' : '2s'} ease infinite`,
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
            <span style={{ fontFamily: 'var(--orbis-mono)', fontSize: 8, color: secondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 }}>
              Entrance: {ss.hoverTransition === '100ms' ? '0.3s' : ss.hoverTransition === '300ms' ? '0.9s' : '0.6s'}
            </span>
          </div>

          {/* Speed comparison — visual indicator */}
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8,
            backgroundColor: cardBg, borderRadius: r, padding: 14,
            border: ss.border || `1px solid ${text}12`,
          }}>
            <span style={{ fontFamily: 'var(--orbis-mono)', fontSize: 8, color: secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Transition
            </span>
            <div style={{ height: 4, borderRadius: 2, backgroundColor: `${text}08`, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 2, backgroundColor: primary,
                width: ss.hoverTransition === '100ms' ? '20%' : ss.hoverTransition === '150ms' ? '33%' : ss.hoverTransition === '200ms' ? '50%' : ss.hoverTransition === '250ms' ? '62%' : '80%',
                transition: 'width 500ms ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: 'var(--orbis-mono)', fontSize: 8, color: `${text}30` }}>fast</span>
              <span style={{ fontFamily: 'var(--orbis-mono)', fontSize: 9, color: text, fontWeight: 600 }}>{ss.hoverTransition || '200ms'}</span>
              <span style={{ fontFamily: 'var(--orbis-mono)', fontSize: 8, color: `${text}30` }}>slow</span>
            </div>

            <span style={{ fontFamily: 'var(--orbis-mono)', fontSize: 8, color: secondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 }}>
              Effects
            </span>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[
                ss.hoverLift !== '0' && 'Lift',
                ss.shadowShift && 'Shadow',
                ss.glowOnHover && 'Glow',
                ss.hoverScale && 'Scale',
                ss.hoverBackdrop && 'Blur',
                'Press',
              ].filter(Boolean).map((tag, i) => (
                <span key={i} style={{
                  fontFamily: 'var(--orbis-mono)', fontSize: 7, textTransform: 'uppercase', letterSpacing: '0.05em',
                  padding: '2px 5px', borderRadius: ss.pillR || '2px',
                  backgroundColor: `${primary}12`, color: primary, fontWeight: 500,
                }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* CSS keyframes for scroll entrance animation */}
        <style>{`
          @keyframes slideInOut {
            0%, 100% { opacity: 0; transform: translateY(8px); }
            20%, 80% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}
